import React, { useEffect, useRef, useState } from "react"
import { InfoCircle } from "react-bootstrap-icons"

interface InfoTooltipProps {
  text: React.ReactNode
  align?: "left" | "right"
  className?: string
  iconSize?: number
}

export const InfoTooltip: React.FC<InfoTooltipProps> = ({
  text,
  align = "right",
  className = "",
  iconSize = 14,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!isOpen) return

    const handleOutsideInteraction = (event: MouseEvent | TouchEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleOutsideInteraction)
    document.addEventListener("touchstart", handleOutsideInteraction)
    return () => {
      document.removeEventListener("mousedown", handleOutsideInteraction)
      document.removeEventListener("touchstart", handleOutsideInteraction)
    }
  }, [isOpen])

  return (
    <span ref={containerRef} className={`relative inline-block ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-label="More info"
        className="inline-flex align-middle text-gray-500 hover:text-gray-700 focus:outline-none"
      >
        <InfoCircle size={iconSize} />
      </button>
      {isOpen && (
        <span
          role="tooltip"
          className={`absolute z-10 top-full mt-1 w-64 text-xs font-normal normal-case leading-relaxed text-left text-white bg-gray-800 rounded p-3 shadow-lg ${
            align === "right" ? "right-0" : "left-0"
          }`}
        >
          {text}
        </span>
      )}
    </span>
  )
}
